import { Component, computed, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CheckCircle, AlertTriangle } from 'lucide-angular';
import { BadgeModule } from 'primeng/badge';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressBarModule } from 'primeng/progressbar';
import { AccordionModule } from 'primeng/accordion';
import { TOOLS } from 'src/app/features/general-agent/constants/tool.constants';
import { IconComponent } from 'src/app/@core/components/ui/icon/icon.component';
import { IToolCall } from 'src/app/features/general-agent/models/tool-call.model';

type DiffType = 'unchanged' | 'added' | 'removed';

interface LineDiff {
    type: DiffType;
    oldLine: string | null;
    newLine: string | null;
    lineNumber: number;
}

interface CharDiffPart {
    text: string;
    type: DiffType;
}

@Component({
    selector: 'app-str-replace-tool-view',
    standalone: true,
    imports: [
        CommonModule,
        BadgeModule,
        TooltipModule,
        ProgressBarModule,
        AccordionModule,
        IconComponent,
    ],
    templateUrl: './str-replace-tool-view.component.html',
    styleUrl: './str-replace-tool-view.component.scss'
})
export class StrReplaceToolViewComponent {
    //#region Inputs
    toolCall = input<Partial<IToolCall>>({});
    //#endregion

    //#region Lucide Icons
    //#endregion

    //#region State
    viewMode = signal<'unified' | 'split'>('unified');
    //#endregion

    //#region Computed values
    toolContent = computed(() => {
        const args = this.toolCall()?.args;
        return args && 'content' in args ? args.content : undefined;
    });
    filePath = computed(() => {
        const args = this.toolCall()?.args;
        return args && 'file_path' in args ? args.file_path : '';
    });
    strReplaceContent = computed(() => {
        const toolCall = this.toolCall();
        if (!toolCall) return { oldStr: '', newStr: '' };
        if (toolCall.name === TOOLS.EDIT_FILE) {
            const args = this.toolCall()?.args;
            const oldStr = args && 'old_string' in args ? args.old_string : '';
            const newStr = args && 'new_string' in args ? args.new_string : '';
            return { oldStr: oldStr || '', newStr: newStr || '' };
        } else if (toolCall.name === TOOLS.WRITE_FILE) {
            const args = this.toolCall()?.args;
            const content = args && 'content' in args ? args.content : '';
            return { oldStr: '', newStr: content || '' };
        }
        return { oldStr: '', newStr: '' };
    });
    oldStr = computed(() => this.strReplaceContent().oldStr);
    newStr = computed(() => this.strReplaceContent().newStr);

    lineDiff = computed(() => {
        const oldStr = this.oldStr();
        const newStr = this.newStr();
        return this.generateLineDiff(oldStr || '', newStr || '');
    });

    diffStats = computed(() => {
        const diff = this.lineDiff();
        return {
            additions: diff.filter(line => line.type === 'added').length,
            deletions: diff.filter(line => line.type === 'removed').length
        };
    });
    //#endregion

    constructor() {
    }

    extractFilePath(content: string | undefined): string | null {
        if (!content) return null;
        try {
            const parsed = JSON.parse(content);
            return parsed.file_name || null;
        } catch {
            return null;
        }
    }

    extractStrReplaceContent(content: string | undefined): {
        oldStr: string | null;
        newStr: string | null;
    } {
        if (!content) return { oldStr: null, newStr: null };
        try {
            const parsed = JSON.parse(content);
            return {
                oldStr: parsed.old_content || null,
                newStr: parsed.new_content || null,
            };
        } catch {
            return { oldStr: null, newStr: null };
        }
    }

    parseNewlines(text: string): string {
        return text.replace(/\\n/g, '\n');
    }

    generateLineDiff(oldText: string, newText: string): LineDiff[] {
        const parsedOldText = this.parseNewlines(oldText);
        const parsedNewText = this.parseNewlines(newText);

        const oldLines = parsedOldText.split('\n');
        const newLines = parsedNewText.split('\n');

        const diffLines: LineDiff[] = [];
        const maxLines = Math.max(oldLines.length, newLines.length);

        for (let i = 0; i < maxLines; i++) {
            const oldLine = i < oldLines.length ? oldLines[i] : null;
            const newLine = i < newLines.length ? newLines[i] : null;

            if (oldLine === newLine) {
                diffLines.push({ type: 'unchanged', oldLine, newLine, lineNumber: i + 1 });
            } else {
                if (oldLine !== null) {
                    diffLines.push({ type: 'removed', oldLine, newLine: null, lineNumber: i + 1 });
                }
                if (newLine !== null) {
                    diffLines.push({ type: 'added', oldLine: null, newLine, lineNumber: i + 1 });
                }
            }
        }

        return diffLines;
    }

    generateCharDiff(oldText: string, newText: string): CharDiffPart[] {
        const parsedOldText = this.parseNewlines(oldText);
        const parsedNewText = this.parseNewlines(newText);

        // Find common prefix length
        let prefixLength = 0;
        while (
            prefixLength < parsedOldText.length &&
            prefixLength < parsedNewText.length &&
            parsedOldText[prefixLength] === parsedNewText[prefixLength]
        ) {
            prefixLength++;
        }
        let oldSuffixStart = parsedOldText.length;
        let newSuffixStart = parsedNewText.length;
        while (
            oldSuffixStart > prefixLength &&
            newSuffixStart > prefixLength &&
            parsedOldText[oldSuffixStart - 1] === parsedNewText[newSuffixStart - 1]
        ) {
            oldSuffixStart--;
            newSuffixStart--;
        }
        const parts: CharDiffPart[] = [];
        if (prefixLength > 0) {
            parts.push({
                text: parsedOldText.substring(0, prefixLength),
                type: 'unchanged',
            });
        }
        if (oldSuffixStart > prefixLength) {
            parts.push({
                text: parsedOldText.substring(prefixLength, oldSuffixStart),
                type: 'removed',
            });
        }
        if (newSuffixStart > prefixLength) {
            parts.push({
                text: parsedNewText.substring(prefixLength, newSuffixStart),
                type: 'added',
            });
        }
        if (oldSuffixStart < parsedOldText.length) {
            parts.push({
                text: parsedOldText.substring(oldSuffixStart),
                type: 'unchanged',
            });
        }
        return parts;
    }

    onTabChange(event: { value: string }): void {
        this.viewMode.set(event.value as 'unified' | 'split');
    }
}
